import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  FormControl,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useState, useEffect } from "react";

import Notifications from "@/components/Notifications/Notifications";
import { dataSources } from "@/components/political/dataSources";
import EventForm from "@/components/political/Uploader/forms/EventForm";
import Uploader from "@/components/political/Uploader/Uploader";
import { useFileSelectorHook } from "@/components/political/Uploader/useFileSelectorHook";
import SiteIcons from "@/components/SiteIcons/SiteIcons";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";

function FileSelector() {
  const { user } = useAuth0();
  const { resetContext } = useUpdateDataContext();
  const [selectedFile, setSelectedFile] = useState(null);
  const [notification, setNotification] = useState({});
  const [event, setEvent] = useState({
    event_id: null,
    event_name: "",
    event_date: null,
  });
  const [allEvents, setAllEvents] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [allowContinue, setAllowContinue] = useState(false);
  const { handleContinue, isProcessing, loading, fetchEvents } =
    useFileSelectorHook(user, dataSource, event, selectedFile, setNotification);

  useEffect(() => {
    const loadEvents = async () => {
      const res = await fetchEvents(user.org_name, true);
      if (res) {
        setAllEvents(res);
      }
    };
    loadEvents();
    // fetchEvents is recreated by useFileSelectorHook every render;
    // intentionally fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // "political" and "solidarity" do not have associated events, so if a user selects an event and then changes data source, we need to reset the event if it's not "member"
    if (!dataSource === "member") {
      setEvent({ event_id: null, event_name: "", event_date: null });
    }
  }, [dataSource]);

  useEffect(() => {
    if (dataSource === "member") {
      setAllowContinue(!!selectedFile && !!event.event_id);
    } else {
      setAllowContinue(!!selectedFile && !!dataSource);
    }
  }, [event, selectedFile, dataSource]);

  useEffect(() => {
    resetContext();
    // resetContext is recreated by UpdateDataContext every render;
    // intentionally fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelection = (selectedFile) => {
    setSelectedFile(selectedFile);
  };

  return (
    <>
      {isProcessing ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            margin: "80px",
          }}
        >
          <Typography sx={{ marginBottom: "30px" }}>Processing...</Typography>
          <SiteIcons iconstring="circular-loader" page="sitewide-loader" />
        </Box>
      ) : (
        <>
          <Box>
            <Box>
              <Typography>Choose one:</Typography>
              {/* DATA SOURCE SELECTOR */}
              <FormControl sx={{ display: "flex" }}>
                <RadioGroup
                  name="data-source-selection"
                  value={dataSource}
                  onChange={(e) => setDataSource(e.target.value)}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  {Object.keys(dataSources).map((key) => {
                    const source = dataSources[key];
                    const SourceIcon = source.icon;
                    // if there are no events, disable the member mobilization button
                    const isDisabled =
                      source.value === "member" &&
                      (!allEvents || !allEvents.length);

                    return (
                      <Box
                        key={source.value}
                        data-testid={`source-container-${source.value}`}
                        onClick={() =>
                          !isDisabled && setDataSource(source.value)
                        }
                        sx={{
                          width: "370px",
                          backgroundColor: "white",
                          padding: "16px 20px",
                          borderRadius: "6px",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.9 : 1,
                          border: "solid 2px",
                          borderColor: isDisabled
                            ? "#e0e0e0"
                            : dataSource === source.value
                              ? "secondary.main"
                              : "#80808054",
                          "&:hover": {
                            borderColor: isDisabled
                              ? "#e0e0e0"
                              : "primary.main",
                          },
                          // turn off pointer events for children so they don't intercept clicks
                          pointerEvents: isDisabled ? "none" : "auto",
                        }}
                      >
                        <FormControlLabel
                          value={source.value}
                          disabled={isDisabled}
                          control={
                            <Radio size="small" sx={{ display: "none" }} />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <SourceIcon
                                sx={{
                                  color: isDisabled
                                    ? "#808080b5 !important"
                                    : source.color,
                                  fontSize: "2.5rem",
                                }}
                              />
                              <Typography
                                sx={{
                                  fontWeight: "500",
                                  fontSize: "1.2rem",
                                  marginLeft: "3px",
                                }}
                              >
                                {source.label}
                              </Typography>
                            </Box>
                          }
                          sx={{ margin: 0, width: "100%" }}
                        />
                        <ul
                          style={{
                            padding: "15px 20px",
                            color: isDisabled
                              ? "rgba(0, 0, 0, 0.38)"
                              : "inherit",
                          }}
                        >
                          {source.description.map((line, index) => (
                            <li key={index}>{line}</li>
                          ))}
                        </ul>
                      </Box>
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </Box>

            <Box>
              {dataSource === "member" && (
                <EventForm
                  event={event}
                  updateEvent={setEvent}
                  allEvents={allEvents}
                />
              )}
            </Box>
          </Box>

          <Uploader
            handleFileSelection={handleFileSelection}
            loading={loading}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              marginBottom: "15px",
            }}
          >
            <Button
              className="siteBlue"
              onClick={handleContinue}
              disabled={!allowContinue}
            >
              Continue
            </Button>
          </Box>
        </>
      )}

      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
}

export default FileSelector;
